import { Md5 } from "ts-md5";
import type { Ref } from "vue";
import { parse as parsePostgresArray } from "postgres-array";
import type { AccessToken } from "@okta/okta-auth-js";
import type { OperationResult } from "@urql/core";
export function clamp(num: number, min: number, max: number): number {
  return Math.min(Math.max(num, min), max);
}

export function postgresToJsonArray(
  data: string | null,
  shouldParseFloat = false
): (string | number | string[] | number[])[] | null {
  if (!data) {
    return [];
  }

  return parsePostgresArray(data, v => (shouldParseFloat ? parseFloat(v) : v));
}

export function jsonToPostgresArray(
  data: (string | number | string[] | number[])[] | null
): string | null {
  if (!data || !data?.length) {
    return "{}";
  }

  return data.reduce<string>((acc, item, idx) => {
    if (idx === 0) {
      acc += "{";
    }

    if (Array.isArray(item)) {
      acc += jsonToPostgresArray(item);
    } else {
      acc += `${item}`;
    }

    if (idx === data.length - 1) {
      acc += "}";
    } else {
      acc += ",";
    }

    return acc;
  }, "");
}

export function hashFields(fields: unknown[]): string {
  const hashId = fields.reduce<string>((str, field) => {
    if (field === undefined || field === null || typeof field === "object")
      return str;

    return str + String(field);
  }, "");

  return Md5.hashStr(hashId);
}

// https://stackoverflow.com/a/29273131
// reverse the above function
export function localizedStringToNumber(
  localizedStr: string,
  localOverride: string | string[] | undefined = undefined
): number {
  const thousandSeparator = Intl.NumberFormat(localOverride)
    .format(11111)
    .replaceAll("1", "");
  const decimalSeparator = Intl.NumberFormat(localOverride)
    .format(1.1)
    .replaceAll("1", "");

  return Number(
    localizedStr
      .replaceAll(thousandSeparator, "")
      .replaceAll(decimalSeparator, ".")
  );
}

export function roundFloat(num: number): number {
  return Number(num.toPrecision(12));
}

export const DEBOUNCE_TIME_MS = 500;

export const retrieveAccessTokenFromLocalStorage = (): AccessToken | null => {
  const tokens = window.localStorage?.getItem("okta-token-storage") || "";
  return tokens ? JSON.parse(tokens).accessToken : null;
};

export function restoreRefsOnMutationError(
  result: Promise<OperationResult>,
  ...objsToRestore: Ref<unknown>[]
): void {
  const clonedData = objsToRestore.map(obj =>
    JSON.parse(JSON.stringify(obj.value))
  );

  result.then(({ error }) => {
    if (error) {
      clonedData.forEach((data, i) => {
        objsToRestore[i].value = data;
      });
    }
  });
}

export function arrayOfInts(numValues: number, start = 0): number[] {
  return new Array(numValues).fill(null).map((_, i) => i + start);
}

export function asyncTimeout(ts: number): Promise<void> {
  return new Promise(resolve => {
    setTimeout(() => {
      return resolve();
    }, ts);
  });
}

export function localStorageGet(key: string): string | null {
  if (typeof localStorage !== "undefined" && localStorage) {
    return localStorage.getItem(key);
  }
  return null;
}

export function localStorageSet(key: string, value: string) {
  if (typeof localStorage !== "undefined" && localStorage) {
    localStorage.setItem(key, value);
  }
}

export function pushToLocalStorageArray<T>(key: string, item: T): Array<T> {
  const existingArray = getLocalStorageArray(key);
  existingArray.push(item);

  localStorageSet(key, JSON.stringify(existingArray));
  return existingArray;
}

export function removeFromLocalStorageArray<T>(
  key: string,
  item: T,
  identifier?: string
): boolean {
  const itemIsObject = typeof item === "object" && !!item;

  if (!itemIsObject && identifier) {
    throw new Error(
      "Identifier should not be provided when item is not an object"
    );
  }

  const existingArray = getLocalStorageArray(key);

  const idx = identifier
    ? existingArray.findIndex(
        (i: Record<string, unknown>) =>
          i[identifier] === (<Record<string, unknown>>item)[identifier]
      )
    : existingArray.indexOf(item);

  const removed = idx > -1;

  if (removed) {
    existingArray.splice(idx, 1);
    localStorageSet(key, JSON.stringify(existingArray));
  }

  return removed;
}

export function getLocalStorageArray(key: string) {
  const a = localStorageGet(key);
  const existingArray = a ? JSON.parse(a) : [];
  return existingArray;
}

export function upsertLocalStorageArray<T>(
  key: string,
  item: T,
  identifier?: string
): Array<T> {
  const existingArray = getLocalStorageArray(key);

  const itemIsObject = typeof item === "object" && !!item;

  if (!itemIsObject && identifier) {
    throw new Error(
      "Identifier should not be provided when item is not an object"
    );
  }

  const idx = identifier
    ? existingArray.findIndex(
        (i: T) =>
          (<Record<string, unknown>>i)[identifier] ===
          (<Record<string, unknown>>item)[identifier]
      )
    : existingArray.indexOf(item);

  if (idx !== -1) {
    existingArray[idx] = item;
  } else {
    existingArray.push(item);
  }

  localStorageSet(key, JSON.stringify(existingArray));
  return existingArray;
}
